import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { getTranslation } from "@/lib/translations";
import DOMPurify from "dompurify";
import kinstaBadge from "@/assets/badges/kinsta-partner.svg";
import { usePageSEO } from "@/hooks/usePageSEO";
import { resolveAssetUrl } from "@/lib/media";

interface Solution {
  id: string;
  title: string;
  title_ar: string | null;
  slug: string;
  description: string | null;
  description_ar: string | null;
  hero_image: string | null;
  thumbnail_image: string | null;
  video_link: string | null;
  affiliate_link: string | null;
  featured_title: string | null;
  featured_title_ar: string | null;
  card_1_title: string | null; card_1_details: string | null; card_1_icon: string | null;
  card_1_title_ar: string | null; card_1_details_ar: string | null;
  card_2_title: string | null; card_2_details: string | null; card_2_icon: string | null;
  card_2_title_ar: string | null; card_2_details_ar: string | null;
  card_3_title: string | null; card_3_details: string | null; card_3_icon: string | null;
  card_3_title_ar: string | null; card_3_details_ar: string | null;
  card_4_title: string | null; card_4_details: string | null; card_4_icon: string | null;
  card_4_title_ar: string | null; card_4_details_ar: string | null;
  card_5_title: string | null; card_5_details: string | null; card_5_icon: string | null;
  card_5_title_ar: string | null; card_5_details_ar: string | null;
  card_6_title: string | null; card_6_details: string | null; card_6_icon: string | null;
  card_6_title_ar: string | null; card_6_details_ar: string | null;
  our_solutions: string | null;
  our_solutions_ar: string | null;
  our_solutions_image: string | null;
  wavai_benefits: string | null;
  wavai_benefits_ar: string | null;
  wavai_benefits_image: string | null;
  cta_title: string | null;
  cta_title_ar: string | null;
  cta_description: string | null;
  cta_description_ar: string | null;
  q1: string | null; a1: string | null; q1_ar: string | null; a1_ar: string | null;
  q2: string | null; a2: string | null; q2_ar: string | null; a2_ar: string | null;
  q3: string | null; a3: string | null; q3_ar: string | null; a3_ar: string | null;
  q4: string | null; a4: string | null; q4_ar: string | null; a4_ar: string | null;
  q5: string | null; a5: string | null; q5_ar: string | null; a5_ar: string | null;
  seo_title: string | null;
  seo_title_ar: string | null;
  seo_description: string | null;
  seo_description_ar: string | null;
  category_id: string | null;
}

interface RelatedSolution {
  id: string;
  title: string;
  title_ar: string | null;
  slug: string;
  thumbnail_image: string | null;
  description: string | null;
  description_ar: string | null;
}

export default function SolutionDetail() {
  const { slug } = useParams();
  const { lang, localePath } = useLanguage();
  const t = (key: string) => getTranslation(key, lang);
  const isAr = lang === "ar";

  const [solution, setSolution] = useState<Solution | null>(null);
  const [related, setRelated] = useState<RelatedSolution[]>([]);
  const [loading, setLoading] = useState(true);

  const ar = <T,>(enVal: T, arVal: T | null | undefined): T => {
    if (isAr && arVal) return arVal;
    return enVal;
  };

  useEffect(() => {
    const fetchSolution = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("solutions")
        .select("*")
        .eq("slug", slug!)
        .maybeSingle();

      setSolution(data);

      if (data?.category_id) {
        const { data: rel } = await supabase
          .from("solutions")
          .select("id, title, title_ar, slug, thumbnail_image, description, description_ar")
          .eq("category_id", data.category_id)
          .neq("id", data.id)
          .limit(4);
        setRelated(rel || []);
      }
      setLoading(false);
    };
    if (slug) fetchSolution();
  }, [slug]);

  const seoData = useMemo(() => {
    if (!solution) return null;
    const title = ar(solution.seo_title, solution.seo_title_ar) || ar(solution.title, solution.title_ar) + " | Wavai";
    const desc = ar(solution.seo_description, solution.seo_description_ar) || ar(solution.description, solution.description_ar) || "";
    const base = "https://www.wavai.ae";
    return {
      title,
      description: desc,
      canonical: `${base}/solutions/${solution.slug}`,
      hreflang: [
        { lang: "en", href: `${base}/solutions/${solution.slug}` },
        { lang: "ar", href: `${base}/ar/solutions/${solution.slug}` },
        { lang: "x-default", href: `${base}/solutions/${solution.slug}` },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: ar(solution.title, solution.title_ar),
        description: desc,
        url: `${base}/solutions/${solution.slug}`,
        applicationCategory: "BusinessApplication",
      },
    };
  }, [solution, lang]);

  usePageSEO(seoData || {
    title: "Solutions | Wavai",
    description: "Explore tools and platforms.",
  });

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-muted-foreground">{t("Loading...")}</p>
      </div>
      <Footer />
    </div>
  );

  if (!solution) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <h1 className="text-2xl font-bold">{t("Solution not found")}</h1>
        <Button asChild><Link to={localePath("/solutions")}>{t("Back to Solutions")}</Link></Button>
      </div>
      <Footer />
    </div>
  );

  const cards = [
    { title: ar(solution.card_1_title, solution.card_1_title_ar), details: ar(solution.card_1_details, solution.card_1_details_ar), icon: solution.card_1_icon },
    { title: ar(solution.card_2_title, solution.card_2_title_ar), details: ar(solution.card_2_details, solution.card_2_details_ar), icon: solution.card_2_icon },
    { title: ar(solution.card_3_title, solution.card_3_title_ar), details: ar(solution.card_3_details, solution.card_3_details_ar), icon: solution.card_3_icon },
    { title: ar(solution.card_4_title, solution.card_4_title_ar), details: ar(solution.card_4_details, solution.card_4_details_ar), icon: solution.card_4_icon },
    { title: ar(solution.card_5_title, solution.card_5_title_ar), details: ar(solution.card_5_details, solution.card_5_details_ar), icon: solution.card_5_icon },
    { title: ar(solution.card_6_title, solution.card_6_title_ar), details: ar(solution.card_6_details, solution.card_6_details_ar), icon: solution.card_6_icon },
  ].filter((c) => c.title);

  const faqs = [
    { q: ar(solution.q1, solution.q1_ar), a: ar(solution.a1, solution.a1_ar) },
    { q: ar(solution.q2, solution.q2_ar), a: ar(solution.a2, solution.a2_ar) },
    { q: ar(solution.q3, solution.q3_ar), a: ar(solution.a3, solution.a3_ar) },
    { q: ar(solution.q4, solution.q4_ar), a: ar(solution.a4, solution.a4_ar) },
    { q: ar(solution.q5, solution.q5_ar), a: ar(solution.a5, solution.a5_ar) },
  ].filter((f) => f.q);

  const displayTitle = ar(solution.title, solution.title_ar);
  const displayDesc = ar(solution.description, solution.description_ar);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 relative">
          <div className="absolute inset-0 bg-gradient-hero opacity-40" />
          <div className="container px-6 relative z-10">
            <Link to={localePath("/solutions")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8">
              <ArrowLeft className="h-4 w-4" /> {t("Back to Solutions")}
            </Link>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                {solution.thumbnail_image && (
                  <img src={resolveAssetUrl(solution.thumbnail_image)} alt={displayTitle} className="h-12 w-auto mb-6" />
                )}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-6">{displayTitle}</h1>
                <p className="text-lg text-muted-foreground mb-8">{displayDesc}</p>
                <div className="flex flex-row gap-3">
                  <Button asChild variant="hero" size="lg">
                    <Link to={localePath("/contact")}>{t("Get Started")}</Link>
                  </Button>
                  {solution.affiliate_link && (
                    <Button asChild variant="outline" size="lg">
                      <a href={solution.affiliate_link} target="_blank" rel="noopener noreferrer" className="gap-2">
                        {t("Visit Website")} <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
              {solution.hero_image && (
                <div className="rounded-2xl overflow-hidden border border-border shadow-card">
                  <img src={resolveAssetUrl(solution.hero_image)} alt={displayTitle} className="w-full h-auto" loading="lazy" />
                </div>
              )}
            </div>
          </div>
        </section>

        {cards.length > 0 && (
          <section className="py-12 lg:py-32">
            <div className="container px-6">
              {(solution.featured_title || solution.featured_title_ar) && (
                <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-12">{ar(solution.featured_title, solution.featured_title_ar)}</h2>
              )}
              <div className={`grid sm:grid-cols-2 ${cards.length > 4 ? 'lg:grid-cols-3' : 'lg:grid-cols-' + Math.min(cards.length, 4)} gap-6`}>
                {cards.map((card, i) => (
                  <div key={i} className="p-6 rounded-2xl bg-card border border-border shadow-card">
                    {card.icon && <img src={resolveAssetUrl(card.icon)} alt="" className="h-10 w-10 mb-4" loading="lazy" />}
                    <h3 className="font-sans font-medium mb-2">{card.title}</h3>
                    <p className="text-sm text-muted-foreground">{card.details}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {(solution.our_solutions || solution.our_solutions_ar) && (
          <section className="py-12 lg:py-32 bg-secondary/30">
            <div className="container px-6">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ar(solution.our_solutions, solution.our_solutions_ar) || "") }} />
                {solution.our_solutions_image && (
                  <div className="rounded-2xl overflow-hidden border border-border shadow-card">
                    <img src={resolveAssetUrl(solution.our_solutions_image)} alt={t("Our solutions")} className="w-full h-auto" loading="lazy" />
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {(solution.wavai_benefits || solution.wavai_benefits_ar) && (
          <section className="py-12 lg:py-32">
            <div className="container px-6">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {solution.wavai_benefits_image && (
                  <div className="rounded-2xl overflow-hidden border border-border shadow-card order-2 lg:order-1">
                    <img src={resolveAssetUrl(solution.wavai_benefits_image)} alt={t("Wavai benefits")} className="w-full h-auto" loading="lazy" />
                  </div>
                )}
                <div className={`${solution.wavai_benefits_image ? 'order-1 lg:order-2' : ''}`}>
                  <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(ar(solution.wavai_benefits, solution.wavai_benefits_ar) || "") }} />
                  {solution.slug === "kinsta" && (
                    <a href="https://kinsta.com/agency-directory/wavai-technology/" target="_blank" rel="noopener noreferrer" className="inline-block mt-8 hover:shadow-md transition-all duration-300">
                      <img src={kinstaBadge} alt="Kinsta Agency Partner" className="h-10 sm:h-12 rounded-md" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {faqs.length > 0 && (
          <section className="py-12 lg:py-32 bg-secondary/30">
            <div className="container px-6 max-w-3xl">
              <h2 className="text-2xl sm:text-3xl font-display font-bold text-center mb-12">{t("Frequently Asked Questions")}</h2>
              <div className="space-y-4">
                {faqs.map((faq, i) => (
                  <details key={i} className="group p-6 rounded-2xl bg-card border border-border">
                    <summary className="font-sans font-medium cursor-pointer list-none flex justify-between items-center">
                      {faq.q}
                      <span className="text-primary ml-4 group-open:rotate-45 transition-transform text-xl">+</span>
                    </summary>
                    <div className="mt-4 text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(faq.a || "") }} />
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {(solution.cta_title || solution.cta_title_ar) && (
          <section className="py-12 lg:py-32">
            <div className="container px-6">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-2xl sm:text-3xl font-display font-bold mb-4">{ar(solution.cta_title, solution.cta_title_ar)}</h2>
                {(solution.cta_description || solution.cta_description_ar) && (
                  <p className="text-lg text-muted-foreground mb-8">{ar(solution.cta_description, solution.cta_description_ar)}</p>
                )}
                <Button asChild variant="hero" size="lg">
                  <Link to={localePath("/contact")}>{t("Contact Us")}</Link>
                </Button>
              </div>
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="py-12 lg:py-32 bg-secondary/30">
            <div className="container px-6">
              <h2 className="text-2xl font-display font-bold mb-8">{t("Related Solutions")}</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={localePath(`/solutions/${r.slug}`)}
                    className="group p-6 rounded-2xl bg-card border border-border hover:border-primary/30 transition-all duration-300 shadow-card hover:shadow-glow flex flex-col items-center text-center"
                  >
                    {r.thumbnail_image && <img src={resolveAssetUrl(r.thumbnail_image)} alt={ar(r.title, r.title_ar)} className="h-10 w-auto mb-3" loading="lazy" />}
                    <h3 className="font-sans font-medium text-sm group-hover:text-primary transition-colors">{ar(r.title, r.title_ar)}</h3>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
